const defaultSerialize = value => JSON.stringify(value)

const assertSectionKey = key => {
    if (typeof key !== 'string' || key.trim().length === 0) {
        throw new TypeError('Dirty state section key must be a non-empty string')
    }
}

const assertStateGetter = getCurrentState => {
    if (typeof getCurrentState !== 'function') {
        throw new TypeError('Dirty state section requires a current-state getter')
    }
}

export default class DirtyStateTracker {
    constructor({ serialize = defaultSerialize } = {}) {
        if (typeof serialize !== 'function') {
            throw new TypeError('Dirty state serializer must be a function')
        }

        this.serialize = serialize
        this.sections = new Map()
    }

    markReady(key, getCurrentState) {
        assertSectionKey(key)
        assertStateGetter(getCurrentState)

        const cleanState = this.serializeCurrentState(key, getCurrentState)

        this.sections.set(key, {
            cleanState,
            getCurrentState,
        })

        return this
    }

    markClean(key) {
        const section = this.resolveSection(key)
        const cleanState = this.serializeCurrentState(key, section.getCurrentState)

        section.cleanState = cleanState

        return this
    }

    markAllClean() {
        const cleanStates = new Map()

        this.sections.forEach((section, key) => {
            cleanStates.set(
                key,
                this.serializeCurrentState(key, section.getCurrentState),
            )
        })

        cleanStates.forEach((cleanState, key) => {
            this.sections.get(key).cleanState = cleanState
        })

        return this
    }

    isReady(key) {
        assertSectionKey(key)
        return this.sections.has(key)
    }

    isChanged(key) {
        const section = this.resolveSection(key)

        return section.cleanState !== this.serializeCurrentState(key, section.getCurrentState)
    }

    hasChanges() {
        for (const [key, section] of this.sections.entries()) {
            if (section.cleanState !== this.serializeCurrentState(key, section.getCurrentState)) {
                return true
            }
        }

        return false
    }

    changedSections() {
        const changed = []

        this.sections.forEach((section, key) => {
            if (section.cleanState !== this.serializeCurrentState(key, section.getCurrentState)) {
                changed.push(key)
            }
        })

        return changed
    }

    readySections() {
        return Array.from(this.sections.keys())
    }

    remove(key) {
        assertSectionKey(key)
        return this.sections.delete(key)
    }

    reset() {
        this.sections.clear()
        return this
    }

    resolveSection(key) {
        assertSectionKey(key)

        const section = this.sections.get(key)
        if (!section) {
            throw new Error(`Dirty state section "${key}" is not ready`)
        }

        return section
    }

    serializeCurrentState(key, getCurrentState) {
        const state = getCurrentState()
        if (state && typeof state.then === 'function') {
            throw new TypeError(`Dirty state section "${key}" getter must be synchronous`)
        }

        const serialized = this.serialize(state, key)
        if (typeof serialized !== 'string') {
            throw new TypeError(`Dirty state serializer must return a string for section "${key}"`)
        }

        return serialized
    }
}
